// Notification event catalog. Each event hangs off a collector data source;
// a rule is only selectable when that collector is enabled in Settings.
// Mirror of the shared/collectors.mjs pattern. Keep dataSource ids in sync
// with shared/collectors.mjs.

export const RECIPIENT_TYPES = Object.freeze({
  FIXED: 'fixed',             // explicit email address(es)
  PEOPLE: 'people',           // specific users (emails resolved from `users`)
  GROUP_OWNERS: 'group-owners', // dynamic; group-scoped events only
  FALLBACK: 'fallback',       // used when dynamic resolution finds nobody
  DEFAULT: 'default',         // the customer's alertEmail
});

function deepFreeze(obj) {
  if (obj && typeof obj === 'object' && !Object.isFrozen(obj)) {
    Object.freeze(obj);
    for (const value of Object.values(obj)) deepFreeze(value);
  }
  return obj;
}

const WATCHLIST_PARAMS = [
  { key: 'watchlist', type: 'string[]', default: [], label: 'Watched group IDs' },
  { key: 'privilegedOnly', type: 'boolean', default: true, label: 'Privileged/role-assignable groups only' },
];

export const NOTIFICATION_EVENTS = deepFreeze([
  { id: 'secure-score-changed', dataSource: 'secure-score', label: 'onGuard score changed', signal: 'high', groupScoped: false, detection: 'live',
    description: 'Score moved up or down by at least the threshold.',
    params: [{ key: 'thresholdPoints', type: 'number', default: 1, min: 1, max: 100, label: 'Minimum change (points)' }] },
  { id: 'secure-score-control-regressed', dataSource: 'secure-score-profiles', label: 'onGuard control regressed', signal: 'high', groupScoped: false, detection: 'live',
    description: 'A specific control got worse versus the previous run.', params: [] },
  { id: 'admin-role-granted', dataSource: 'user-roles', label: 'Admin role granted', signal: 'high', groupScoped: false, detection: 'live',
    description: 'A user gained an Entra admin role (Global Admin, etc.).', params: [] },
  { id: 'admin-role-removed', dataSource: 'user-roles', label: 'Admin role removed', signal: 'high', groupScoped: false, detection: 'live',
    description: 'A user lost an Entra admin role.', params: [] },
  { id: 'group-member-added', dataSource: 'group-memberships', label: 'Member added to group', signal: 'high', groupScoped: true, detection: 'live',
    description: 'A user was added to a watched/privileged group.', params: WATCHLIST_PARAMS },
  { id: 'group-member-removed', dataSource: 'group-memberships', label: 'Member removed from group', signal: 'high', groupScoped: true, detection: 'live',
    description: 'A user was removed from a watched/privileged group.', params: WATCHLIST_PARAMS },
  { id: 'group-owner-changed', dataSource: 'group-memberships', label: 'Group owner changed', signal: 'high', groupScoped: true, detection: 'live',
    description: 'An owner was added to or removed from a watched/privileged group.', params: WATCHLIST_PARAMS },
  { id: 'conditional-access-changed', dataSource: 'conditional-access', label: 'Conditional Access policy changed', signal: 'high', groupScoped: false, detection: 'pending',
    description: 'A CA policy was created, modified, or disabled.', params: [] },
  { id: 'oauth-consent-granted', dataSource: 'oauth2-grants', label: 'New OAuth consent granted', signal: 'high', groupScoped: false, detection: 'pending',
    description: 'A new delegated permission/consent was granted to an app.', params: [] },
  { id: 'risky-user-detected', dataSource: 'risky-users', label: 'Risky user detected', signal: 'high', groupScoped: false, detection: 'pending',
    description: 'A user was flagged at risk or raised to high.', params: [] },
  { id: 'mfa-coverage-drop', dataSource: 'registration-details', label: 'MFA coverage dropped', signal: 'high', groupScoped: false, detection: 'pending',
    description: 'Fewer users are registered for MFA than the previous run.', params: [] },
]);

export const ALL_NOTIFICATION_EVENT_IDS = Object.freeze(NOTIFICATION_EVENTS.map((e) => e.id));
export const NOTIFICATION_EVENTS_BY_ID = Object.freeze(Object.fromEntries(NOTIFICATION_EVENTS.map((e) => [e.id, e])));

/** Events whose data source is enabled. With { liveOnly:true }, only events whose
 *  detection is implemented (so the editor never offers a silently-dead rule). */
export function eventsForCollectors(enabledCollectorIds, { liveOnly = false } = {}) {
  const enabled = new Set(enabledCollectorIds);
  return NOTIFICATION_EVENTS.filter(
    (e) => enabled.has(e.dataSource) && (!liveOnly || e.detection === 'live')
  );
}
